import { Suspense, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Box,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
// Icons.
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded';

import {
  dashboardDialogStyles,
  widgetCardStyle,
} from '../../screens/dashboard/DashboardSx';
import LineChart from './widgets/LineChart/LineChart';
import PieChart from './widgets/PieChart/PieChart';
import NeedHelpWidget from './widgets/system-widgets/NeedHelp/NeedHelp';
import IncidentStatus from './widgets/system-widgets/SoftwareAndServiceUpdates/IncidentStatus';
import BarChart from './widgets/BarChart/BarChart';
import TableWidget from './widgets/TableWidget/TableWidget';
import FunnelChart from './widgets/FunnelChart/FunnelChart';
import DetailsCard from './widgets/DetailCard/DetailsCard';

import WidgetLoadingIndicator from './components/WidgetLoadingIndicator';
import WidgetError from './components/WidgetError';
import BubbleChart from './widgets/BubbleChart/BubbleChart';
import { useTranslation } from 'react-i18next';
import { bloxMarketingColors } from 'themes/constants';
import WidgetMask from './components/WidgetMask';
import { useWidgetHashTarget } from './components/useWidgetHashTarget';
import { useWidgetContext } from './Widget';
import { WidgetCardProps } from './types/widget';
import { hideWidgetActions } from './hooks/useDashboardWidgetHelpers';
import WidgetHeader, { getWidgetTitle } from './components/WidgetHeader';

const WidgetCard = ({ viewportRef, isIndependentSystemWidget }: WidgetCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { widget, isLoading, isError, isMasked, chartType } = useWidgetContext();
  const isHashTarget = useWidgetHashTarget(widget?.id);
  const [isOpen, setIsOpen] = useState(false);
  const widgetModule = widget?.module?.id;
  const allowExpand = false;

  return (
    <Suspense fallback={<WidgetLoadingIndicator />}>
      <Card
        data-testid={`widget-card-${widget?.module?.id}`}
        ref={viewportRef}
        id={`widget-${widget?.id}`}
        sx={[
          widgetCardStyle(theme, isIndependentSystemWidget, widgetModule),
          isHashTarget && { border: `2px solid ${bloxMarketingColors.primary}` },
        ]}
      >
        <CardContent>
          <WidgetHeader
            title={getWidgetTitle(widget, t, isIndependentSystemWidget)}
            testIdPrefix="widget-header"
            className="widget-header"
            allowExpand={allowExpand}
            setIsExpanded={setIsOpen}
            isHidden={hideWidgetActions({ widgetModule }) || false}
            hideAlertButton={
              hideWidgetActions({ widgetModule }) ||
              chartType === 'table' ||
              chartType === 'detailscard'
            }
            renderLoadingChip={!isIndependentSystemWidget}
          />
          {/* Widget Content. */}
          <ErrorBoundary fallback={<WidgetError />}>
            {!!isLoading && !isIndependentSystemWidget ? (
              <WidgetLoadingIndicator
                height={widget?.id === 'insights.trendingcontent' ? 424 : 200}
              />
            ) : !!isError && !isIndependentSystemWidget ? (
              <WidgetError />
            ) : (
              <Box
                data-testid={`widget-card-content--${widget?.id}`}
                className="widget-content"
                sx={{
                  position: 'relative',
                  maxWidth: 'calc(100vw - 60px)', // 60px accounts for left nav.
                  // Allow overflow for dropdowns, tooltips, etc. (https://townnews.atlassian.net/browse/CMSADMIN-2002?focusedCommentId=352056)
                  overflow: 'visible',
                }}
              >
                {chartType === 'line' && <LineChart />}
                {chartType === 'pie' && <PieChart />}
                {chartType === 'table' && !isIndependentSystemWidget && <TableWidget />}
                {chartType === 'funnel' && <FunnelChart />}
                {isIndependentSystemWidget && widgetModule === 'help' && (
                  <NeedHelpWidget />
                )}
                {isIndependentSystemWidget && widgetModule === 'status' && (
                  <IncidentStatus />
                )}
                {chartType === 'bar' && <BarChart />}
                {chartType === 'detailscard' && <DetailsCard />}
                {chartType === 'bubble' && <BubbleChart />}
                {chartType === null && !isIndependentSystemWidget && (
                  <WidgetError type="error" />
                )}

                {/* Masking */}
                {isMasked && <WidgetMask title={widget?.title} />}
              </Box>
            )}
          </ErrorBoundary>
        </CardContent>
        <Dialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          maxWidth="xl"
          sx={dashboardDialogStyles(theme)}
        >
          <DialogContent>
            <Box className="dialog-header">
              <Typography
                className="dialog-title"
                variant="h6"
                component="span"
              >
                {getWidgetTitle(widget, t, isIndependentSystemWidget)}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setIsOpen(false)}
              >
                <CloseFullscreenRoundedIcon fontSize="small" />
              </IconButton>
            </Box>
          </DialogContent>
        </Dialog>
      </Card>
    </Suspense>
  );
};

export default WidgetCard;
